import React, { useEffect, useRef, useState } from "react";
import {
  ArrowLeft,
  Save,
  Plus,
  Loader2,
  FileText,
  Upload,
  Menu,
  Type,
  Settings2,
  FileCheck2,
  Search,
  CheckCircle2,
} from "lucide-react";
import { useNavigate,useLocation, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import adminApi from "./adminApi";

// import { CKEditor } from "@ckeditor/ckeditor5-react";
// import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import {
  ClassicEditor,
  Essentials,
  Paragraph,
  Heading,
  Bold,
  Italic,
  Underline,
  Font,
  Alignment,
  List,
  Link,
  BlockQuote,
  Table as CKTable,
  TableToolbar,
  Image as CKImage,
  ImageUpload,
  ImageToolbar,
  ImageStyle,
  ImageResize,
  SimpleUploadAdapter,
  HorizontalLine,
} from "ckeditor5";
import "ckeditor5/ckeditor5.css";
const SEQUENCES = Array.from({ length: 31 }, (_, i) => i + 1);

const emptyForm = {
  parent_pageid: "",
  page_name: "",
  seq_id: "",
  ischecked: false,

  page_title: "",
  page_contents: "",

  seo_metatag: "",
  seo_metatitle: "",
  seo_metadesc: "",
  seo_keyword: "",

  ischeckedpdf: false,
};

export default function ManageSubMenuForm() {
  const navigate = useNavigate();
  // const { id } = useParams();
  const location = useLocation();

  const id = location.state?.page_id;

  const isEdit = Boolean(id);

  const [parents, setParents] = useState([]);
  const [form, setForm] = useState(emptyForm);

  const [file, setFile] = useState(null);
  const [filePreviewName, setFilePreviewName] = useState(null);
  const [existingAttachment, setExistingAttachment] = useState(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  // NEW: sequence loading state (per-parent next-sequence lookup)
  const [seqLoading, setSeqLoading] = useState(false);

  const fileInputRef = useRef(null);

  const [toast, setToast] = useState(null);

  // =========================================================
  // Toast
  // =========================================================

  const showToast = (type, message) => {
    setToast({
      type,
      message,
    });

    setTimeout(() => {
      setToast(null);
    }, 2600);
  };

  // =========================================================
  // Fetch Parents
  // =========================================================

  const fetchParents = async () => {
    try {
      const res = await adminApi.get("/admin/sub-menu/meta/parents");

      setParents(res.data.parents || []);
    } catch (err) {
      console.error("Could not load parent menu list:", err);

      setFormError("Could not load menu list.");
    }
  };

  // =========================================================
  // Fetch Edit Data
  // =========================================================

  const fetchEditData = async () => {
    if (!id) return;

    setLoading(true);
    setFormError("");

    try {
      const res = await adminApi.get(`/admin/sub-menu/${id}`);

      const d = res.data.item;

      setForm({
        parent_pageid: d.parent_pageid || "",
        page_name: d.page_name || "",
        seq_id: d.seq_id || "",

        ischecked: !!d.ischecked,

        page_title: d.page_title || "",
        page_contents: d.page_contents || "",

        seo_metatag: d.seo?.seo_metatag || "",
        seo_metatitle: d.seo?.seo_metatitle || "",
        seo_metadesc: d.seo?.seo_metadesc || "",
        seo_keyword: d.seo?.seo_keyword || "",

        ischeckedpdf: !!d.ischeckedpdf,
      });

      setExistingAttachment(d.attachment || null);
    } catch (err) {
      setFormError(
        err.response?.data?.message ||
          "Could not load this record."
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // Initial Load
  // =========================================================

  useEffect(() => {
    fetchParents();

    if (isEdit) {
      fetchEditData();
    }
  }, [id]);

  // =========================================================
  // Update Field
  // =========================================================

  const updateField = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // =========================================================
  // Next Sequence For Selected Parent
  // =========================================================
  // Fires only when the user actively changes the "Menu Name"
  // dropdown (not on the initial programmatic load in edit mode),
  // so it never clobbers a record's existing seq_id on page open.

  const handleParentChange = async (e) => {
    const parentId = e.target.value;

    updateField("parent_pageid", parentId);

    if (!parentId) {
      updateField("seq_id", "");
      return;
    }

    setSeqLoading(true);

    try {
      const res = await adminApi.get("/admin/sub-menu", {
        params: { parent: parentId },
      });

      const items = res.data.items || [];

      // Highest existing sequence among sub-menus under this parent.
      // Exclude the record currently being edited so it doesn't
      // count itself when its parent hasn't actually changed.
      const maxSequence = items.reduce((max, item) => {
        if (isEdit && String(item.page_id) === String(id)) return max;

        const sequence = Number(item.seq_id) || 0;

        return Math.max(max, sequence);
      }, 0);

      updateField("seq_id", maxSequence + 1);
    } catch (err) {
      console.error("Could not get next sequence for this menu:", err);

      // Fall back to 1 if the lookup fails, rather than leaving a stale value.
      updateField("seq_id", 1);
    } finally {
      setSeqLoading(false);
    }
  };

  // =========================================================
  // File Change
  // =========================================================

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];

    if (!f) return;

    if (!f.name.toLowerCase().endsWith(".pdf")) {
      setFormError("Only PDF files are allowed.");
      return;
    }

    if (f.size > 2 * 1024 * 1024) {
      setFormError("PDF file size must be less than 2MB.");
      return;
    }

    setFormError("");

    setFile(f);
    setFilePreviewName(f.name);
  };

  // =========================================================
  // Submit
  // =========================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    setFormError("");

    // -------------------------------
    // Validation
    // -------------------------------

    if (!form.parent_pageid) {
      setFormError("Menu Name is required.");
      return;
    }

    if (!form.page_name.trim()) {
      setFormError("Sub Menu Name is required.");
      return;
    }

    if (!form.seq_id) {
      setFormError("Sequence is required.");
      return;
    }

    if (
      form.ischecked &&
      (!form.page_title.trim() ||
        !form.page_contents.trim())
    ) {
      setFormError(
        "Title and Contents are required when 'Add content for this menu' is checked."
      );

      return;
    }

    if (
      form.ischeckedpdf &&
      !file &&
      !existingAttachment
    ) {
      setFormError(
        "A PDF file is required when 'Add PDF for this menu' is checked."
      );

      return;
    }

    // -------------------------------
    // FormData
    // -------------------------------

    const fd = new FormData();

    fd.append(
      "parent_pageid",
      form.parent_pageid
    );

    fd.append(
      "page_name",
      form.page_name.trim()
    );

    fd.append(
      "seq_id",
      form.seq_id
    );

    fd.append(
      "ischecked",
      form.ischecked ? "1" : "0"
    );

    fd.append(
      "page_title",
      form.page_title.trim()
    );

    fd.append(
      "page_contents",
      form.page_contents.trim()
    );

    fd.append(
      "seo_metatag",
      form.seo_metatag
    );

    fd.append(
      "seo_metatitle",
      form.seo_metatitle
    );

    fd.append(
      "seo_metadesc",
      form.seo_metadesc
    );

    fd.append(
      "seo_keyword",
      form.seo_keyword
    );

    fd.append(
      "ischeckedpdf",
      form.ischeckedpdf ? "1" : "0"
    );

    if (file) {
      fd.append("attachment", file);
    }

    // -------------------------------
    // Save
    // -------------------------------

    setSaving(true);

    try {
      if (isEdit) {
        await adminApi.put(
          `/admin/sub-menu/${id}`,
          fd
        );

        showToast(
          "success",
          "Sub menu content updated successfully."
        );
      } else {
        await adminApi.post(
          "/admin/sub-menu",
          fd
        );

        showToast(
          "success",
          "Sub menu content added successfully."
        );
      }

      setTimeout(() => {
        navigate("/admin/contents/sub-menu");
      }, 700);
    } catch (err) {
      setFormError(
        err.response?.data?.message ||
          "Something went wrong. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  // =========================================================
  // Back
  // =========================================================

  const handleBack = () => {
    if (saving) return;

    navigate("/admin/contents/sub-menu");
  };

  // =========================================================
  // Loading
  // =========================================================

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white shadow-sm border border-slate-200 flex items-center justify-center">
            <Loader2
              size={20}
              className="animate-spin text-[#0EA5A4]"
            />
          </div>

          <span className="text-sm text-slate-500">
            Loading...
          </span>
        </div>
      </div>
    );
  }

  // =========================================================
  // UI
  // =========================================================

  return (
    <div className="h-full flex flex-col bg-slate-50">

      {/* =====================================================
          HEADER
      ===================================================== */}

      <div className="shrink-0 px-1" style={{
border: "1px solid #087875",
paddingTop: "0px",
paddingBottom: "15px",
marginBottom: "15px",
      }}>

        <div className="flex items-center justify-between">

          {/* Left */}

          <div className="flex items-center gap-3"
          style={{
            marginLeft: "10px",
          }}
          >

            <div>

              <div className="flex items-center gap-2 mt-3">

                <h3 className="font-display font-bold text-[18px] text-[#0B2545]"
                style={{marginRight:"10px"}}
                >

                  {isEdit
                    ? "Edit Sub Menu Content"
                    : "Add Sub Menu Content"}

                </h3>

                <span
                  className="
                    hidden sm:inline-flex
                    items-center gap-1
                    px-2 py-1
                    rounded-full
                    text-[10px]
                    font-semibold
                    bg-[#0EA5A4]/10
                    text-[#0EA5A4]
                  "
                >
                  {isEdit ? "EDIT" : "NEW"}
                </span>

              </div>

              <p className="text-xs text-slate-400 mt-1 d-none">

                {isEdit
                  ? "Update sub menu information and content"
                  : "Create a new sub menu with content"}

              </p>

            </div>

          </div>

          {/* Right */}

          <div className="hidden md:flex items-center gap-2" style={{marginRight:"15px",marginTop:"15px"}}>

            <div className="flex items-center gap-2 text-[11px] text-slate-400">

              <div className="" />

                <button
              type="button"
              onClick={handleBack}
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition
              "
            >
              <ArrowLeft size={18} /> Back
            </button>

            </div>

          </div>

        </div>

      </div>

      {/* =====================================================
          MAIN
      ===================================================== */}

      <div className="flex-1 min-h-0 overflow-auto pb-8">

        <form onSubmit={handleSubmit}>

          {/* <div className="max-w-5xl mx-auto space-y-5"> */}
            <div className="mx-auto space-y-5" style={{borderRadius:"10px"}}>

            {/* =================================================
                BASIC INFORMATION
            ================================================= */}

            <div className="bg-white  border border-slate-200 shadow-sm overflow-hidden">

              {/* Section Header */}

              <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3 d-none">

                <div className="w-9 h-9 rounded-lg bg-[#0B2545]/5 flex items-center justify-center">

                  <Menu
                    size={17}
                    className="text-[#0B2545]"
                  />

                </div>

                <div>

                  <h4 className="text-sm font-semibold text-slate-800">
                    Basic Information
                  </h4>

                  <p className="text-[11px] text-slate-400 mt-0.5">
                    Set the parent menu and sub menu details
                  </p>

                </div>

              </div>

              {/* Body */}

              <div className="p-1">
<table className="table table-bordered" style={{marginBottom:"1px", tableLayout:"fixed", width:"100%"}}>
  

  <tbody>
    <tr>
      <td style={{width:"10%"}}>
       <label className="block text-xs font-semibold text-slate-600 labelStly">
        Menu Name <span className="text-red-500">*</span>
      </label>

        </td>
      <td style={{width:"28%"}}>  <div className="relative">

                      <select
                        value={form.parent_pageid}
                        onChange={handleParentChange}
                        className="
                          w-full
                          appearance-none
                          bg-slate-50
                          border border-slate-200
                          rounded-xl
                          px-4 py-3
                          pr-10
                          text-sm
                          text-slate-700
                          outline-none
                          focus:bg-white
                          focus:border-[#0EA5A4]
                          focus:ring-4
                          focus:ring-[#0EA5A4]/10
                          transition
                          h-[44px]
                        "
                      >

                        <option value="">
                          -- Select Menu --
                        </option>

                        {parents.map((p) => (
                          <option
                            key={p.page_id}
                            value={p.page_id}
                          >
                            {p.page_name}
                          </option>
                        ))}

                      </select>

                      <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="m6 9 6 6 6-6" />
                        </svg>
                      </div>

                    </div></td>
      <td style={{width:"10%"}}>
        <label className="block text-xs font-semibold text-slate-600 labelStly">
          Sequence <span className="text-red-500">*</span>
        </label>
                    </td>
      <td style={{width:"20%"}}>
                    <div className="relative">

                      <select
                        value={form.seq_id}
                        onChange={(e) =>
                          updateField(
                            "seq_id",
                            e.target.value
                          )
                        }
                        disabled={seqLoading || !form.parent_pageid}
                        className="
                          w-full
                          appearance-none
                          bg-slate-50
                          border border-slate-200
                          rounded-xl
                          px-4 py-3
                          pr-10
                          text-sm
                          text-slate-700
                          outline-none
                          focus:bg-white
                          focus:border-[#0EA5A4]
                          focus:ring-4
                          focus:ring-[#0EA5A4]/10
                          transition
                          h-[44px]
                          disabled:bg-slate-100
                          disabled:cursor-not-allowed
                        "
                      >

                        {seqLoading ? (

                          <option value="">
                            {/* Loading sequence... */}
                          </option>

                        ) : !form.parent_pageid ? (

                          <option value="">
                            Select a menu first
                          </option>

                        ) : (

                          <>
                            <option value="">
                              -- Select --
                            </option>

                            {SEQUENCES.map((s) => (
                              <option
                                key={s}
                                value={s}
                              >
                                {s}
                              </option>
                            ))}

                            {/*
                                If the computed/loaded sequence is beyond 31,
                                keep it selectable instead of silently dropping it.
                            */}
                            {Number(form.seq_id) > 31 && (

                              <option value={form.seq_id}>
                                {form.seq_id}
                              </option>

                            )}
                          </>

                        )}

                      </select>

                      <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="m6 9 6 6 6-6" />
                        </svg>
                      </div>

                    </div></td>
      <td style={{width:"12%"}}> 
        <label className="block text-xs font-semibold text-slate-600 labelStly">
          Sub Menu Name <span className="text-red-500">*</span>
        </label>
                    </td>
      <td><div className="relative">

                      <Type
                        size={16}
                        className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
                      />

                      <input
                        type="text"
                        value={form.page_name}
                        onChange={(e) =>
                          updateField(
                            "page_name",
                            e.target.value
                          )
                        }
                        placeholder="Enter sub menu name"
                        className="
                          w-full
                          bg-slate-50
                          border border-slate-200
                          rounded-xl
                          pl-10 pr-4 py-3
                          text-sm
                          text-slate-700
                          placeholder:text-slate-400
                          outline-none
                          focus:bg-white
                          focus:border-[#0EA5A4]
                          focus:ring-4
                          focus:ring-[#0EA5A4]/10
                          transition
                          h-[44px]
                        "
                      />

                    </div></td>
    </tr>

    
  </tbody>
</table>
             
              </div>

            </div>

            {/* =================================================
                CONTENT SECTION
            ================================================= */}

            <div className="bg-white  border border-slate-200 shadow-sm overflow-hidden">

              {/* Header */}

              <div className="px-6 py-4 border-b border-slate-100">

                <div className="flex items-center justify-between gap-4">

                  <div className="flex items-center gap-3">

                    <div className="w-9 h-9 rounded-lg bg-[#0EA5A4]/10 flex items-center justify-center">

                      <FileText
                        size={17}
                        className="text-[#0EA5A4]"
                      />

                    </div>

                    <div>

                      <h4 className="text-sm font-semibold text-slate-800">
                        Page Content
                      </h4>

                      <p className="text-[11px] text-slate-400 mt-0.5">
                        Add title, Content and SEO information
                      </p>

                    </div>

                  </div>

                  {/* Toggle */}

                  <label className="flex items-center gap-2.5 cursor-pointer select-none">

                    <span className="hidden sm:block text-xs font-medium text-slate-500">
                      Enable Content
                    </span>

                    <div className="relative">

                      <input
                        type="checkbox"
                        checked={form.ischecked}
                        onChange={(e) =>
                          updateField(
                            "ischecked",
                            e.target.checked
                          )
                        }
                        className="sr-only peer"
                      />

                      <div
                        className="
                          w-10 h-5
                          bg-slate-200
                          rounded-full
                          peer
                          peer-checked:bg-[#0EA5A4]
                          transition
                        "
                      />

                      <div
                        className="
                          absolute
                          left-0.5 top-0.5
                          w-4 h-4
                          bg-white
                          rounded-full
                          shadow-sm
                          transition-transform
                          peer-checked:translate-x-5
                        "
                      />

                    </div>

                  </label>

                </div>

              </div>

              {/* Content Body */}

              <AnimatePresence initial={false}>

                {form.ischecked && (

                  <motion.div
                    initial={{
                      height: 0,
                      opacity: 0,
                    }}
                    animate={{
                      height: "auto",
                      opacity: 1,
                    }}
                    exit={{
                      height: 0,
                      opacity: 0,
                    }}
                    transition={{
                      duration: 0.25,
                    }}
                    className="overflow-hidden"
                    style={{marginTop:"-30px"}}
                  >

                    <div className="p-6">
<table className="table table-bordered" style={{marginBottom:"1px"}}>
  

  <tbody>
    <tr>
      <td style={{width:"10%"}}>
        <label className="block text-xs font-semibold text-slate-600 labelStly">
          Page Title <span className="text-red-500">*</span>
        </label>
      </td>
      <td>

                        <input
                          type="text"
                          value={form.page_title}
                          onChange={(e) =>
                            updateField(
                              "page_title",
                              e.target.value
                            )
                          }
                          placeholder="Enter page title"
                          className="
                            w-full
                            bg-slate-50
                            border border-slate-200
                            rounded-xl
                            px-4 py-3
                            text-sm
                            outline-none
                            text-slate-700
                            placeholder:text-slate-400
                            focus:bg-white
                            focus:border-[#0EA5A4]
                            focus:ring-4
                            focus:ring-[#0EA5A4]/10
                            transition
                            h-[44px]
                          "
                        />
      </td>
      </tr>
      <tr>
      <td style={{width:"10%"}}><label className="block text-xs font-semibold text-slate-600 labelStly">

                      Page Contents

                    

                    </label>
      </td>
      <td>
        <div
                          className="
                            rounded-xl
                            overflow-hidden
                            border border-slate-200
                            focus-within:border-[#0EA5A4]
                            focus-within:ring-4
                            focus-within:ring-[#0EA5A4]/10
                            transition
                          "
                        >

                          {/* <CKEditor
                            editor={ClassicEditor}
                            data={form.page_contents}
                            onChange={(event, editor) => {
                              const data =
                                editor.getData();

                              updateField(
                                "page_contents",
                                data
                              );
                            }}
                          /> */}

                          <CKEditor
        editor={ClassicEditor}
        data={form.page_contents}
        config={{
        licenseKey: "GPL",
       plugins: [
          Essentials, Paragraph, Heading,
          Bold, Italic, Underline,
          Font, Alignment,
          List, Link, BlockQuote,
          CKTable, TableToolbar,
          CKImage, ImageUpload, ImageToolbar, ImageStyle, ImageResize,
          SimpleUploadAdapter,
          HorizontalLine,
        ],
       toolbar: [
          "heading", "|",
          "fontFamily", "fontSize", "|",
          "bold", "italic", "underline", "|",
          "fontColor", "fontBackgroundColor", "|",
          "alignment", "|",
          "bulletedList", "numberedList", "|",
          "link", "blockQuote", "insertTable", "uploadImage", "horizontalLine", "|",
          "undo", "redo",
        ],
        fontFamily: {
          options: [
            "default",
            "Arial, Helvetica, sans-serif",
            "Georgia, serif",
            "Times New Roman, Times, serif",
            "Courier New, Courier, monospace",
            "Verdana, Geneva, sans-serif",
            "Tahoma, Geneva, sans-serif",
            "Trebuchet MS, Helvetica, sans-serif",
          ],
          supportAllValues: true,
        },
        fontSize: {
          options: [9, 11, 13, "default", 17, 19, 21, 24, 28, 32],
          supportAllValues: true,
        },
        table: {
          contentToolbar: ["tableColumn", "tableRow", "mergeTableCells"],
        },
        image: {
          toolbar: [
            "imageStyle:alignLeft",
            "imageStyle:alignRight",
            "imageStyle:block",
            "|",
            "toggleImageCaption",
            "imageTextAlternative",
            "resizeImage",
          ],
        },
        simpleUpload: {
          uploadUrl: `${adminApi.defaults.baseURL}/admin/sub-menu/upload-image`,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("mkcg_admin_token")}`,
          },
        },
      }}
  onChange={(event, editor) => {
    const data = editor.getData();
    updateField("page_contents", data);
  }}
/>

                        </div>
                    </td>
                    
  </tr>

    
  </tbody>
</table>

                      {/* =================================================
                          SEO
                      ================================================= */}

                      <div className="mt-4">

                        <div className="flex items-center gap-3 mb-4">

                          <div className="h-px bg-slate-200 flex-1" />

                          <div className="flex items-center gap-2 px-3">

                            <Search
                              size={15}
                              className="text-[#0EA5A4]"
                            />

                            <span className="text-xs font-bold uppercase tracking-wider text-[#0EA5A4]">
                              SEO Details
                            </span>

                          </div>

                          <div className="h-px bg-slate-200 flex-1" />

                        </div>
<table className="table table-bordered" style={{marginBottom:"1px"}}>
  <tbody>
    <tr>
      <td style={{width:"10%"}}>
        <label className="block text-xs font-semibold text-slate-600  labelStly">
          Meta Tags
        </label>
      </td>
      <td>
  <textarea
                              rows={3}
                              value={form.seo_metatag}
                              onChange={(e) =>
                                updateField(
                                  "seo_metatag",
                                  e.target.value
                                )
                              }
                              placeholder="Enter meta tags"
                              className="
                                w-full
                                bg-slate-50
                                border border-slate-200
                                rounded-xl
                                px-4 py-3
                                text-sm
                                resize-none
                                outline-none
                                text-slate-700
                                placeholder:text-slate-400
                                focus:bg-white
                                focus:border-[#0EA5A4]
                                focus:ring-4
                                focus:ring-[#0EA5A4]/10
                                transition
                              "
                            />
      </td>
       <td style={{width:"10%"}}>
        <label className="block text-xs font-semibold text-slate-600  labelStly">
          Meta Title
        </label>
      </td>
      <td>
<textarea
                              rows={3}
                              value={form.seo_metatitle}
                              onChange={(e) =>
                                updateField(
                                  "seo_metatitle",
                                  e.target.value
                                )
                              }
                              placeholder="Enter meta title"
                              className="
                                w-full
                                bg-slate-50
                                border border-slate-200
                                rounded-xl
                                px-4 py-3
                                text-sm
                                resize-none
                                outline-none
                                text-slate-700
                                placeholder:text-slate-400
                                focus:bg-white
                                focus:border-[#0EA5A4]
                                focus:ring-4
                                focus:ring-[#0EA5A4]/10
                                transition
                              "
                            />

      </td>
      </tr>
      <tr>
      <td style={{width:"10%"}}>
        <label className="block text-xs font-semibold text-slate-600 labelStly">
          Meta Description
        </label>
      </td>
      <td>
<textarea
                              rows={4}
                              value={form.seo_metadesc}
                              onChange={(e) =>
                                updateField(
                                  "seo_metadesc",
                                  e.target.value
                                )
                              }
                              placeholder="Enter meta description"
                              className="
                                w-full
                                bg-slate-50
                                border border-slate-200
                                rounded-xl
                                px-4 py-3
                                text-sm
                                resize-none
                                outline-none
                                text-slate-700
                                placeholder:text-slate-400
                                focus:bg-white
                                focus:border-[#0EA5A4]
                                focus:ring-4
                                focus:ring-[#0EA5A4]/10
                                transition
                              "
                            />
      </td>
       <td style={{width:"10%"}}>
        <label className="block text-xs font-semibold text-slate-600  labelStly">
          Keywords
        </label>
      </td>
      <td>
<textarea
                              rows={4}
                              value={form.seo_keyword}
                              onChange={(e) =>
                                updateField(
                                  "seo_keyword",
                                  e.target.value
                                )
                              }
                              placeholder="Enter keywords separated by commas"
                              className="
                                w-full
                                bg-slate-50
                                border border-slate-200
                                rounded-xl
                                px-4 py-3
                                text-sm
                                resize-none
                                outline-none
                                text-slate-700
                                placeholder:text-slate-400
                                focus:bg-white
                                focus:border-[#0EA5A4]
                                focus:ring-4
                                focus:ring-[#0EA5A4]/10
                                transition
                              "
                            />
      </td>
  </tr>
  </tbody>
</table>
                        

                      </div>

                    </div>

                  </motion.div>

                )}

              </AnimatePresence>

            </div>

            {/* =================================================
                PDF SECTION
            ================================================= */}

            <div className="bg-white  border border-slate-200 shadow-sm overflow-hidden">

              {/* Header */}

              <div className="px-6 py-4 border-b border-slate-100">

                <div className="flex items-center justify-between gap-4">

                  <div className="flex items-center gap-3">

                    <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center">

                      <FileCheck2
                        size={17}
                        className="text-red-500"
                      />

                    </div>

                    <div>

                      <h4 className="text-sm font-semibold text-slate-800">
                        PDF Attachment
                      </h4>

                      <p className="text-[11px] text-slate-400 mt-0.5">
                        Upload a PDF document for this sub menu
                      </p>

                    </div>

                  </div>

                  {/* Toggle */}

                  <label className="flex items-center gap-2.5 cursor-pointer select-none">

                    <span className="hidden sm:block text-xs font-medium text-slate-500">
                      Enable PDF
                    </span>

                    <div className="relative">

                      <input
                        type="checkbox"
                        checked={form.ischeckedpdf}
                        onChange={(e) =>
                          updateField(
                            "ischeckedpdf",
                            e.target.checked
                          )
                        }
                        className="sr-only peer"
                      />

                      <div
                        className="
                          w-10 h-5
                          bg-slate-200
                          rounded-full
                          peer
                          peer-checked:bg-[#0EA5A4]
                          transition
                        "
                      />

                      <div
                        className="
                          absolute
                          left-0.5 top-0.5
                          w-4 h-4
                          bg-white
                          rounded-full
                          shadow-sm
                          transition-transform
                          peer-checked:translate-x-5
                        "
                      />

                    </div>

                  </label>

                </div>

              </div>

              {/* PDF Body */}

              <AnimatePresence initial={false}>

                {form.ischeckedpdf && (

                  <motion.div
                    initial={{
                      height: 0,
                      opacity: 0,
                    }}
                    animate={{
                      height: "auto",
                      opacity: 1,
                    }}
                    exit={{
                      height: 0,
                      opacity: 0,
                    }}
                    transition={{
                      duration: 0.25,
                    }}
                    className="overflow-hidden"
                    style={{marginTop:"-30px"}}
                  >

                    <div className="p-6">

                      <label className="block text-xs font-semibold text-slate-600 mb-2">

                        PDF File

                        <span className="text-red-500 ml-1">
                          *
                        </span>

                      </label>

                      <div
                        onClick={() =>
                          fileInputRef.current?.click()
                        }
                        className="
                          group
                          border-2
                          border-dashed
                          border-slate-200
                          hover:border-[#0EA5A4]
                          bg-slate-50
                          hover:bg-[#0EA5A4]/5
                          
                          px-6 py-7
                          cursor-pointer
                          transition-all
                        "
                      >

                        <div className="flex flex-col sm:flex-row items-center gap-4">

                          <div
                            className="
                              w-14 h-14
                             
                              bg-white
                              border border-slate-200
                              group-hover:border-[#0EA5A4]/30
                              flex items-center justify-center
                              shrink-0
                              transition
                            "
                          >

                            <FileText
                              size={25}
                              className="text-slate-300 group-hover:text-[#0EA5A4] transition"
                            />

                          </div>

                          <div className="flex-1 text-center sm:text-left">

                            <div className="text-sm font-semibold text-slate-700">

                              {filePreviewName
                                ? filePreviewName
                                : existingAttachment
                                ? "Click to replace existing PDF"
                                : "Choose PDF file"}

                            </div>

                            <div className="text-xs text-slate-400 mt-1">

                              PDF only • Maximum file size 2MB

                            </div>

                            {existingAttachment &&
                              !filePreviewName && (

                                <div className="inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 rounded-md bg-[#0EA5A4]/10 text-[11px] text-[#0EA5A4]">

                                  <CheckCircle2 size={12} />

                                  Existing:
                                  {" "}
                                  {existingAttachment}

                                </div>

                              )}

                          </div>

                          <div
                            className="
                              flex items-center gap-2
                              px-4 py-2.5
                              rounded-lg
                              bg-white
                              border border-slate-200
                              text-xs
                              font-medium
                              text-slate-600
                              group-hover:text-[#0EA5A4]
                              group-hover:border-[#0EA5A4]/30
                              transition
                            "
                          >

                            <Upload size={15} />

                            Browse

                          </div>

                        </div>

                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".pdf,application/pdf"
                          onChange={handleFileChange}
                          className="hidden"
                        />

                      </div>

                    </div>

                  </motion.div>

                )}

              </AnimatePresence>

            </div>

            {/* =================================================
                ERROR
            ================================================= */}

            <AnimatePresence>

              {formError && (

                <motion.div
                  initial={{
                    opacity: 0,
                    y: -5,
                  }}
                  animate={{
                    opacity: 1,
                    y: 0,
                  }}
                  exit={{
                    opacity: 0,
                    y: -5,
                  }}
                  className="
                    flex items-start gap-3
                    bg-red-50
                    border border-red-100
                    rounded-xl
                    px-4 py-3
                  "
                >

                  <div className="w-7 h-7 rounded-lg bg-red-100 flex items-center justify-center shrink-0">

                    <span className="text-red-600 text-xs font-bold">
                      !
                    </span>

                  </div>

                  <div>

                    <p className="text-xs font-semibold text-red-700">
                      Please check the form
                    </p>

                    <p className="text-xs text-red-600 mt-0.5">
                      {formError}
                    </p>

                  </div>

                </motion.div>

              )}

            </AnimatePresence>

            {/* =================================================
                ACTION BAR
            ================================================= */}

            <div
              className="
                bg-white
                
                border border-slate-200
                shadow-sm
                px-5 py-4
                flex flex-col sm:flex-row
                items-center
                justify-between
                gap-4
              "
            >

              <div className="flex items-center gap-2 text-xs text-slate-400">

                <Settings2 size={15} />

                <span>
                  Review all information before saving
                </span>

              </div>

              <div className="flex items-center gap-3 w-full sm:w-auto">

                <button
                  type="button"
                  onClick={handleBack}
                  disabled={saving}
                  className="
                    flex-1 sm:flex-none
                    px-5 py-2.5
                    rounded-xl
                    border border-slate-200
                    bg-white
                    text-sm
                    font-medium
                    text-slate-600
                    hover:bg-slate-50
                    transition
                    disabled:opacity-50
                  "
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={saving}
                  className="
                    flex-1 sm:flex-none
                    min-w-[130px]
                    flex items-center justify-center gap-2
                    px-6 py-2.5
                    rounded-xl
                    bg-[#0B2545]
                    hover:bg-[#143A63]
                    text-white
                    text-sm
                    font-semibold
                    shadow-sm
                    hover:shadow-md
                    transition-all
                    disabled:opacity-60
                    disabled:cursor-not-allowed
                  "
                >

                  {saving ? (
                    <>
                      <Loader2
                        size={16}
                        className="animate-spin"
                      />

                      Saving...
                    </>
                  ) : isEdit ? (
                    <>
                      <Save size={16} />

                      Update
                    </>
                  ) : (
                    <>
                      <Plus size={16} />

                      Submit
                    </>
                  )}

                </button>

              </div>

            </div>

          </div>

        </form>

      </div>

      {/* =====================================================
          TOAST
      ===================================================== */}

      <AnimatePresence>

        {toast && (

          <motion.div
            initial={{
              opacity: 0,
              y: 20,
              scale: 0.95,
            }}
            animate={{
              opacity: 1,
              y: 0,
              scale: 1,
            }}
            exit={{
              opacity: 0,
              y: 20,
              scale: 0.95,
            }}
            className="
              fixed
              bottom-6
              right-6
              z-50
              flex
              items-center
              gap-3
              px-4
              py-3
              rounded-xl
              shadow-xl
              text-sm
              font-medium
              bg-[#0B2545]
              text-white
            "
          >

            <div className="w-7 h-7 rounded-lg bg-white/10 flex items-center justify-center">

              <CheckCircle2
                size={16}
                className="text-[#5EEAD4]"
              />

            </div>

            {toast.message}

          </motion.div>

        )}

      </AnimatePresence>

    </div>
  );
}
